import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import SignUp from "./pages/SignUp";
import { Circles } from "react-loader-spinner";
import SignIn from "./pages/SignIn";
import Account from "./pages/Account";
import RecoverPassword from "./pages/RecoverPassword";
import ResetPassword from "./pages/ResetPassword";
import Article from "./pages/Article";
import AboutUs from "./pages/AboutUs";
import UserDashboard from "./pages/UserDashboard";
import Profile from "./pages/Profile";
import Quotation from "./pages/Quotation";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccess";
import Lead from "./pages/Lead";
import { routes } from "./contant";
import SupportPage from "./pages/Support";
import PrivacyPolicy from "./pages/policy";
import RequestDetail from "./components/RequestDetail";
import ProLeadPartnerAgreement from "./components/ProLeadPartnerAgreement";
import RenewPage from "./pages/renewPage";
import TermsAndConditions from "./pages/TermsAndConditions";
import AdminDashboard from "./pages/dashboard/AdminDashboard";


import { jwtDecode } from "jwt-decode";
import { getToken, isTokenExpired, logoutUser } from "./utils/auth";

import { verifyPasswordResetCode, checkActionCode } from "firebase/auth";

import { auth } from "./config/firebaseConfig";
import { ToastContainer, toast } from "react-toastify";
import "../src/style/popup.css";

/* ================= PUBLIC ROUTE ================= */
const PublicRoute = ({ children }) => {
  const token = getToken();

  if (token && !isTokenExpired(token)) {
    return <Navigate to="/" />;
  }

  return children;
};

/* ================= PRIVATE ROUTE ================= */
const PrivateRoute = ({ children }) => {
  const token = getToken();

  if (!token || isTokenExpired(token)) {
    logoutUser();
    return <Navigate to="/signin" />;
  }

  return children;
};

/* ================= VERIFY ================= */
const Verify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const verifyAction = async () => {
      try {
        await checkActionCode(auth, oobCode);

        if (mode === "verifyEmail") {
          const email = await verifyPasswordResetCode(auth, oobCode);
          navigate(`/account?email=${encodeURIComponent(email)}`);
        }

        if (mode === "resetPassword") {
          const email = await verifyPasswordResetCode(auth, oobCode);
          navigate(`/reset-password?email=${encodeURIComponent(email)}`);
        }
      } catch (error) {
        toast.error("Invalid or expired link");
        navigate("/recover-password");
      }
    };

    verifyAction();
  }, [mode, oobCode, navigate]);

  return (
    <>
      <ToastContainer />
      <div>Processing...</div>
    </>
  );
};

/* ================= MAIN APP ================= */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const location = useLocation();

  /* AUTH + LOADER */
  useEffect(() => {
    const token = getToken();

    if (token) {
      const { exp } = jwtDecode(token);
      const remaining = exp * 1000 - Date.now();

      if (remaining <= 0) {
        logoutUser();
      } else {
        setTimeout(logoutUser, remaining);
        setIsAuthenticated(true);
      }
    } else {
      setIsAuthenticated(false);
    }

    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  /* POPUP LOGIC */
  useEffect(() => {
    if (!loading && location.pathname === "/signup") {
      const shown = localStorage.getItem("popupShown");

      if (!shown) {
        const timer = setTimeout(() => {
          setShowPopup(true);
          localStorage.setItem("popupShown", "true");
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [loading, location.pathname]);

  /* LOADER */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Circles height="40" width="40" color="#1E90FF" />
      </div>
    );
  }

  /* UI */
  return (
    <>
      {/* POPUP */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box notice">
            <h2 className="popup-title">Important Notice</h2>

            <p className="popup-message">
              LeadGenPro is not a lead providing platform. It is only a lead
              generation (lead collection) platform.
            </p>

            <p className="popup-message">
              Professional insurance agents associated with insurance
              aggregators should not register on this platform.
            </p>

            <p className="popup-message">
              Please proceed only if you agree to this condition.
            </p>

            <div className="popup-checkbox">
              <input
                type="checkbox"
                id="confirm"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              />
              <label htmlFor="confirm">
                I confirm that I am not a professional insurance agent and I
                understand LeadGenPro does not provide leads.
              </label>
            </div>

            <div className="popup-actions">
              <button
                className="btn cancel"
                onClick={() => (window.location.href = "/")}
              >
                Cancel
              </button>

              <button
                className="btn continue"
                disabled={!isChecked}
                onClick={() => setShowPopup(false)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ROUTES */}
      <Routes>
        <Route
          path={routes.main}
          element={
            <PrivateRoute>
              <UserDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.article}
          element={
            isAuthenticated ? <Article /> : <Navigate to={routes.aboutUs} />
          }
        />

        <Route
          path={routes.renewpage}
          element={
            <PrivateRoute>
              <RenewPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/prolead-agreement"
          element={<ProLeadPartnerAgreement />}
        />

        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        <Route path="/terms" element={<TermsAndConditions />} />

        <Route path={routes.aboutUs} element={<AboutUs />} />

        <Route path={routes.support} element={<SupportPage />} />

        <Route
          path={routes.dashboard}
          element={
            <PrivateRoute>
              <UserDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.adminDashboard}
          element={
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.lead}
          element={
            <PrivateRoute>
              <Lead />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.payment}
          element={
            <PrivateRoute>
              <PaymentPage />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.paymentsuccess}
          element={
            <PrivateRoute>
              <PaymentSuccessPage />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.requestDetail}
          element={
            <PrivateRoute>
              <RequestDetail />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.quotation}
          element={
            <PrivateRoute>
              <Quotation />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.account}
          element={
            <PublicRoute>
              <Account />
            </PublicRoute>
          }
        />

        <Route
          path={routes.profile}
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route
          path={routes.signup}
          element={
            <PublicRoute>
              <SignUp />
            </PublicRoute>
          }
        />

        <Route
          path={routes.signin}
          element={
            <PublicRoute>
              <SignIn />
            </PublicRoute>
          }
        />

        <Route path={routes.recoverPassword} element={<RecoverPassword />} />

        <Route path={routes.resetPassword} element={<ResetPassword />} />

        <Route path="/verify" element={<Verify />} />

        <Route path="/" element={<Navigate to={routes.main} />} />
      </Routes>
    </>
  );
}

/* ================= WRAPPER ================= */
function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;
